export type ConstructionFamily='aurelios'|'xyloria'|'capital'
export type ConstructionKind='threshold'|'tower'|'wall'|'support'|'ritual'|'monument'|'ruin'|'utility'|'natural'|'actor'
export type ConstructionFunction='circulation'|'defense'|'load-bearing'|'ritual'|'memorial'|'infrastructure'|'ruin'|'natural'|'actor'

export type ConstructionInput={
  id:string
  asset:string
  position:readonly [number,number,number]
  height:number
  motion?:'static'|'hover'|'fly'
}

export type ConstructionBlueprint={
  id:string
  asset:string
  kind:ConstructionKind
  purpose:ConstructionFunction
  structural:boolean
  loadBearing:boolean
  foundationRadius:number
  supportCount:number
  supportHeight:number
  access:boolean
  accessStartZ:number
  accessEndZ:number
  utilityGlow:boolean
}

const clamp=(v:number,min:number,max:number)=>Math.max(min,Math.min(max,v))
const text=(asset:string)=>asset.toLowerCase().replace(/[^a-z0-9]+/g,' ')

export function classifyConstructionAsset(asset:string):ConstructionKind{
  const n=text(asset)
  if(/leviathan|dragon|wyvern|titan|phoenix|matriarch|astryx|petragon|colossus|maw/.test(n))return'actor'
  if(/boulder|rock|plant|flora|tree|crystal plant|moon rock/.test(n))return'natural'
  if(/gateway|gate|arch/.test(n))return'threshold'
  if(/tower|obelisk|spire|fortress|bastion|citadel/.test(n))return'tower'
  if(/wall|corner|barricade/.test(n))return'wall'
  if(/column|pillar/.test(n))return'support'
  if(/temple|altar|sanctum|cathedral|shrine/.test(n))return'ritual'
  if(/guardian|statue/.test(n))return'monument'
  if(/ruin/.test(n))return'ruin'
  return'utility'
}

function purposeFor(kind:ConstructionKind):ConstructionFunction{
  if(kind==='threshold')return'circulation'
  if(kind==='tower'||kind==='wall')return'defense'
  if(kind==='support')return'load-bearing'
  if(kind==='ritual')return'ritual'
  if(kind==='monument')return'memorial'
  if(kind==='ruin')return'ruin'
  if(kind==='natural')return'natural'
  if(kind==='actor')return'actor'
  return'infrastructure'
}

export function constructionBlueprint(input:ConstructionInput,family:ConstructionFamily):ConstructionBlueprint{
  const kind=classifyConstructionAsset(input.asset)
  const motion=input.motion??'static'
  const structural=motion==='static'&&!['natural','actor','utility'].includes(kind)
  const loadBearing=structural&&['threshold','tower','wall','support','ritual'].includes(kind)
  const radius=structural?clamp(input.height*.2,.55,2.25):0
  const supportCount=!loadBearing?0:kind==='tower'?4:kind==='wall'||kind==='threshold'||kind==='ritual'?2:1
  const supportHeight=loadBearing?clamp(input.height*.38,.45,2.9):0
  const access=structural&&['threshold','ritual','tower'].includes(kind)&&input.position[2]<-7
  const accessEndZ=-6.15
  const accessStartZ=Math.min(accessEndZ,input.position[2]+radius*.7)
  return{
    id:input.id,asset:input.asset,kind,purpose:purposeFor(kind),structural,loadBearing,
    foundationRadius:radius,
    supportCount,
    supportHeight,
    access,
    accessStartZ,
    accessEndZ,
    utilityGlow:structural&&(family==='xyloria'||kind==='ritual'||kind==='threshold'),
  }
}

export function constructionPalette(family:ConstructionFamily){
  if(family==='xyloria')return{foundation:'#102d31',structure:'#24484b',accent:'#70ffd0'}
  if(family==='capital')return{foundation:'#18212c',structure:'#2b3747',accent:'#55d9ff'}
  return{foundation:'#30271e',structure:'#55422e',accent:'#e2b56f'}
}

export function constructionCombatClearance(blueprint:ConstructionBlueprint){
  return !blueprint.access||blueprint.accessEndZ<=-5.5
}
